import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import CustomSelect from '../../../ui/CustomSelect';

const PUNISHMENT_TYPE_OPTIONS = [
  { value: 'verbal_warning', label: 'Teguran Lisan' },
  { value: 'written_warning_1', label: 'SP 1' },
  { value: 'written_warning_2', label: 'SP 2' },
  { value: 'written_warning_3', label: 'SP 3' },
  { value: 'suspension', label: 'Skorsing' },
  { value: 'termination', label: 'Pemberhentian' },
];

const VIOLATION_CATEGORY_OPTIONS = [
  { value: 'attendance', label: 'Kehadiran' },
  { value: 'discipline', label: 'Disiplin' },
  { value: 'performance', label: 'Kinerja' },
  { value: 'ethics', label: 'Etika' },
  { value: 'safety', label: 'Keselamatan' },
  { value: 'other', label: 'Lainnya' },
];

interface PunishmentInfoTabProps {
  formData: any;
  handleChange: (field: string, value: any) => void;
  employees: any[];
  readonly?: boolean;
}

// Employee Autocomplete Component - stores ID but displays name
function EmployeeAutocomplete({
  value,
  onChange,
  placeholder,
  employees,
  readonly = false,
}: {
  value: number | string | null;
  onChange: (id: number | null) => void;
  placeholder: string;
  employees: any[];
  readonly?: boolean;
}) {
  const [searchText, setSearchText] = useState<string>('');
  const [suggestions, setSuggestions] = useState<any[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [error, setError] = useState<string>('');
  const inputRef = useRef<HTMLInputElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);

  // Get employee name by ID
  const getEmployeeName = useCallback(
    (id: number | string | null): string => {
      if (!id) return '';
      const emp = employees.find(e => e.id === Number(id));
      return emp?.full_name || '';
    },
    [employees]
  );

  // Check if search text matches an employee
  const validateEmployee = useCallback(
    (text: string): boolean => {
      if (!text.trim()) return true; // Empty is valid (will be handled by required validation)
      const trimmedText = text.trim();
      return employees.some(emp => emp.full_name.toLowerCase() === trimmedText.toLowerCase());
    },
    [employees]
  );

  // Initialize search text with employee name if value exists
  useEffect(() => {
    if (value) {
      const name = getEmployeeName(value);
      setSearchText(name);
      setError(''); // Clear error when value is set
    } else {
      setSearchText('');
      setError('');
    }
  }, [value, employees, getEmployeeName]);

  // Filter employees based on input
  useEffect(() => {
    if (searchText && searchText.trim().length > 0 && !readonly) {
      const searchTerm = searchText.toLowerCase().trim();
      const filtered = employees
        .filter(emp => emp.full_name.toLowerCase().includes(searchTerm))
        .slice(0, 5); // Limit to 5 suggestions
      setSuggestions(filtered);
      setShowSuggestions(filtered.length > 0 && searchTerm.length > 0);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  }, [searchText, employees, readonly]);

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        suggestionsRef.current &&
        !suggestionsRef.current.contains(e.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(e.target as Node)
      ) {
        setShowSuggestions(false);
        // Validate when clicking outside
        if (searchText.trim() && !validateEmployee(searchText)) {
          setError('Pegawai tidak ditemukan');
          onChange(null);
        } else if (value) {
          setSearchText(getEmployeeName(value));
          setError('');
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [value, employees, searchText, onChange, getEmployeeName, validateEmployee]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const text = e.target.value;
    setSearchText(text);
    setError(''); // Clear error while typing
    // Clear value if text doesn't match any employee
    if (!text.trim()) {
      onChange(null);
    }
  };

  const handleSelectSuggestion = (employee: any) => {
    onChange(employee.id);
    setSearchText(employee.full_name);
    setShowSuggestions(false);
    setError(''); // Clear error when selecting from suggestions
  };

  const calculateSuggestionsPosition = () => {
    if (!inputRef.current) return null;
    const rect = inputRef.current.getBoundingClientRect();
    return {
      position: 'fixed' as const,
      top: rect.bottom + 4,
      left: rect.left,
      width: rect.width,
    };
  };

  return (
    <div className="relative">
      <input
        ref={inputRef}
        type="text"
        value={searchText}
        onChange={handleChange}
        onFocus={() => {
          if (suggestions.length > 0) {
            setShowSuggestions(true);
          }
        }}
        onBlur={() => {
          // Small delay to allow suggestion click to register first
          setTimeout(() => {
            // Validate on blur
            if (searchText.trim() && !validateEmployee(searchText)) {
              setError('Pegawai tidak ditemukan');
              onChange(null);
            } else if (value && searchText.trim() && searchText.trim() !== getEmployeeName(value)) {
              // If value exists but text doesn't match, reset to employee name
              setSearchText(getEmployeeName(value));
              setError('');
            } else if (!searchText.trim() && value) {
              // If text is empty but value exists, restore the name
              setSearchText(getEmployeeName(value));
              setError('');
            }
          }, 200);
        }}
        onKeyDown={e => {
          if (e.key === 'Escape') {
            setShowSuggestions(false);
            if (value) {
              setSearchText(getEmployeeName(value));
              setError('');
            }
          }
        }}
        className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent ${
          error ? 'border-red-500' : 'border-gray-300'
        }`}
        placeholder={placeholder}
        disabled={readonly}
        autoComplete="off"
      />
      {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
      {showSuggestions &&
        suggestions.length > 0 &&
        calculateSuggestionsPosition() &&
        createPortal(
          <div
            ref={suggestionsRef}
            style={calculateSuggestionsPosition()}
            className="z-[99999] bg-white border border-gray-200 rounded-md shadow-lg max-h-48 overflow-y-auto animate-dropdown-in"
          >
            {suggestions.map(emp => (
              <div
                key={emp.id}
                onClick={() => handleSelectSuggestion(emp)}
                className="w-full px-3 py-2 text-left hover:bg-gray-50 cursor-pointer text-gray-700"
              >
                {emp.full_name}
              </div>
            ))}
          </div>,
          document.body
        )}
    </div>
  );
}

export default function PunishmentInfoTab({
  formData,
  handleChange,
  employees,
  readonly = false,
}: PunishmentInfoTabProps) {
  // const employeeOptions = employees.map(emp => ({ value: emp.id, label: emp.full_name }));

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold">Data Hukuman Disiplin</h3>
        <p className="text-gray-500 text-sm">Informasi detail hukuman pegawai</p>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Pegawai <span className="text-red-500">*</span>
          </label>
          <EmployeeAutocomplete
            value={formData.employee_id}
            onChange={id => handleChange('employee_id', id)}
            placeholder="Ketik nama pegawai..."
            employees={employees}
            readonly={readonly}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Jenis Hukuman <span className="text-red-500">*</span>
          </label>
          <CustomSelect
            value={formData.punishment_type}
            onChange={v => handleChange('punishment_type', v)}
            placeholder="Pilih jenis"
            width="w-full"
            options={PUNISHMENT_TYPE_OPTIONS}
            disabled={readonly}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Kategori Pelanggaran <span className="text-red-500">*</span>
          </label>
          <CustomSelect
            value={formData.violation_category}
            onChange={v => handleChange('violation_category', v)}
            placeholder="Pilih kategori"
            width="w-full"
            options={VIOLATION_CATEGORY_OPTIONS}
            disabled={readonly}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Tanggal Kejadian <span className="text-red-500">*</span>
          </label>
          <input
            type="date"
            value={formData.incident_date}
            onChange={e => handleChange('incident_date', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
            required
            disabled={readonly}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Tanggal Berlaku</label>
          <input
            type="date"
            value={formData.effective_date}
            onChange={e => handleChange('effective_date', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
            disabled={readonly}
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Tanggal Berakhir</label>
          <input
            type="date"
            value={formData.end_date}
            onChange={e => handleChange('end_date', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
            disabled={readonly}
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Deskripsi Pelanggaran
        </label>
        <textarea
          value={formData.description}
          onChange={e => handleChange('description', e.target.value)}
          rows={3}
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
          disabled={readonly}
        />
      </div>
    </div>
  );
}
